import {
  BoilerEfficiencyPoint,
  BoilerGraphJsonMetadata,
  BoilerLoadCurvePoint,
} from './boiler-graph-extract.util';

/**
 * Parse boiler graph metadata from OCR text, API responses, or pasted spec sheets.
 */
export function parseBoilerGraphFromRecognizedText(
  raw: string,
): BoilerGraphJsonMetadata | null {
  if (!raw?.trim()) {
    return null;
  }

  const text = raw.replace(/\s+/g, ' ');
  const upper = text.toUpperCase();

  const manufacturer = detectManufacturer(upper);
  const capacityKgPerHour = detectCapacityKgPerHour(text, upper);
  if (!capacityKgPerHour) {
    return null;
  }

  const graphAxisType =
    /LOAD\s*%|LOAD\s*\(|STEAM\s*CAPA|PERFORMANCE\s*CURVE/i.test(text) &&
    !/STANDARD\s*SIZE|600\s*[-–]\s*8000/i.test(upper)
      ? 'loadPercent'
      : 'steamProduction';

  const loadCurve =
    graphAxisType === 'loadPercent'
      ? detectLoadCurveFromText(text)
      : undefined;
  const efficiencyCurve =
    graphAxisType === 'steamProduction'
      ? detectSteamCurveFromText(text)
      : undefined;

  let maxEfficiencyPercent = detectPeakEfficiency(text);
  let steamProductionKgH = detectSteamAtPeak(text, capacityKgPerHour);

  if (loadCurve?.length) {
    const peak = loadCurve.reduce((b, p) =>
      p.efficiencyPercent > b.efficiencyPercent ? p : b,
    );
    if (maxEfficiencyPercent == null) {
      maxEfficiencyPercent = peak.efficiencyPercent;
    }
    if (steamProductionKgH == null) {
      steamProductionKgH = peak.steamProductionKgH;
    }
  } else if (efficiencyCurve?.length) {
    const peak = efficiencyCurve.reduce((b, p) =>
      p.efficiencyPercent > b.efficiencyPercent ? p : b,
    );
    if (maxEfficiencyPercent == null) {
      maxEfficiencyPercent = peak.efficiencyPercent;
    }
    if (steamProductionKgH == null) {
      steamProductionKgH = peak.steamProductionKgH;
    }
  }

  return {
    manufacturer,
    label: manufacturer ? `${manufacturer} (parsed from graph text)` : 'Parsed from graph',
    capacityKgPerHour,
    graphAxisType,
    maxEfficiencyPercent,
    steamProductionKgH,
    steamProductionRangeMin: 600,
    steamProductionRangeMax: 8000,
    efficiencyCurve,
    loadCurve,
  };
}

function detectManufacturer(upper: string): string {
  if (/KANGRIM|KAN[\s-]*GRIM/.test(upper)) {
    return 'KANGRIM';
  }
  if (/AALBORG|MISSION/.test(upper)) {
    return 'AALBORG INDUSTRIES';
  }
  if (/ALFA\s*LAVAL/.test(upper)) {
    return 'ALFA LAVAL';
  }
  if (/MIURA|SAACKE|KANIS|COCHRAN/.test(upper)) {
    const m = upper.match(/(MIURA|SAACKE|KANIS|COCHRAN)/);
    return m ? m[1] : '';
  }
  return '';
}

function detectCapacityKgPerHour(text: string, upper: string): number {
  const tonHr = text.match(/(\d{1,2}(?:\.\d+)?)\s*TON\s*\/\s*HR/i);
  if (tonHr) {
    return Math.round(Number(tonHr[1]) * 1000);
  }
  const ton = upper.match(/(\d{1,2})\s*TON/);
  if (ton) {
    return Number(ton[1]) * 1000;
  }
  const stdSize = text.match(/STANDARD\s*SIZE[:\s]*(\d{4,6})/i);
  if (stdSize) {
    return Number(stdSize[1]);
  }
  const kgHr = text.match(/(\d{4,6})\s*KG\s*\/?\s*H/i);
  if (kgHr) {
    return Number(kgHr[1]);
  }
  const at100 = text.match(/100\s*%[^\d]*(\d{4,6})/i);
  if (at100) {
    return Number(at100[1]);
  }
  return 0;
}

function detectPeakEfficiency(text: string): number | undefined {
  const effs = [...text.matchAll(/(\d{2}(?:\.\d+)?)\s*%/g)]
    .map((m) => Number(m[1]))
    .filter((n) => n >= 75 && n <= 95);
  return effs.length ? Math.max(...effs) : undefined;
}

function detectSteamAtPeak(
  text: string,
  capacityKgPerHour: number,
): number | undefined {
  const steamVals = [...text.matchAll(/(\d{3,5})\s*(?:KG\s*\/?\s*H|KG\/HR|KG\/H|KG\/HR)/gi)]
    .map((m) => Number(m[1]))
    .filter((n) => n > 0 && n <= capacityKgPerHour * 1.1);

  if (steamVals.length) {
    const inRange = steamVals.filter((n) => n >= 500 && n <= capacityKgPerHour);
    return inRange.length
      ? inRange.reduce((a, b) =>
          Math.abs(b - capacityKgPerHour * 0.5) <
          Math.abs(a - capacityKgPerHour * 0.5)
            ? b
            : a,
        )
      : undefined;
  }

  const plain = [...text.matchAll(/\b(\d{3,5})\b/g)]
    .map((m) => Number(m[1]))
    .filter(
      (n) =>
        n >= 500 &&
        n <= capacityKgPerHour &&
        !isFuelOrConsumptionNumber(text, n),
    );
  return plain.length ? plain[0] : undefined;
}

/** Exclude fuel LHV (e.g. 10700 kcal/kg) and consumption table values. */
function isFuelOrConsumptionNumber(text: string, value: number): boolean {
  if (value >= 10000 && /KCAL|L\.?H\.?V|FUEL\s*OIL|CONSUMPTION/i.test(text)) {
    return true;
  }
  if (/10700|1324|21524/.test(String(value)) && /CONSUMPTION|KCAL/i.test(text)) {
    return true;
  }
  return false;
}

function detectLoadCurveFromText(text: string): BoilerLoadCurvePoint[] | undefined {
  const loads = [...text.matchAll(/\b(12|20|30|40|50|60|70|80|90|100)\s*%/g)].map(
    (m) => Number(m[1]),
  );
  if (loads.length < 3) {
    return undefined;
  }

  const steamNumbers = [...text.matchAll(/\b(\d{3,5})\b/g)]
    .map((m) => Number(m[1]))
    .filter(
      (n) =>
        n >= 1000 &&
        n <= 30000 &&
        !isFuelOrConsumptionNumber(text, n),
    );

  const effNumbers = [...text.matchAll(/(\d{2}(?:\.\d+)?)\s*%/g)]
    .map((m) => Number(m[1]))
    .filter((n) => n >= 75 && n <= 95);

  if (steamNumbers.length < 3) {
    return undefined;
  }

  const points: BoilerLoadCurvePoint[] = [];
  const uniqueLoads = [...new Set(loads)].sort((a, b) => a - b);
  for (let i = 0; i < uniqueLoads.length && i < steamNumbers.length; i++) {
    points.push({
      loadPercent: uniqueLoads[i],
      steamProductionKgH: steamNumbers[i],
      efficiencyPercent: effNumbers[i] ?? effNumbers[effNumbers.length - 1] ?? 84,
    });
  }
  return points.length >= 3 ? points : undefined;
}

function detectSteamCurveFromText(text: string): BoilerEfficiencyPoint[] | undefined {
  const steamVals = [...text.matchAll(/\b(\d{3,5})\b/g)]
    .map((m) => Number(m[1]))
    .filter((n) => n >= 400 && n <= 25000);
  const effVals = [...text.matchAll(/(\d{2}(?:\.\d+)?)\s*%/g)]
    .map((m) => Number(m[1]))
    .filter((n) => n >= 60 && n <= 95);

  if (steamVals.length < 2 || effVals.length < 2) {
    return undefined;
  }

  const len = Math.min(steamVals.length, effVals.length, 12);
  const points: BoilerEfficiencyPoint[] = [];
  for (let i = 0; i < len; i++) {
    points.push({
      steamProductionKgH: steamVals[i],
      efficiencyPercent: effVals[i],
    });
  }
  return points.length >= 2 ? points : undefined;
}

export function parseGeminiBoilerJsonResponse(raw: string): BoilerGraphJsonMetadata | null {
  let text = raw.trim();
  text = text.replace(/```json|```/gi, '').trim();
  const jsonStart = text.indexOf('{');
  const jsonEnd = text.lastIndexOf('}');
  if (jsonStart >= 0 && jsonEnd > jsonStart) {
    text = text.slice(jsonStart, jsonEnd + 1);
  }
  try {
    const parsed = JSON.parse(text) as BoilerGraphJsonMetadata;
    return parsed?.capacityKgPerHour || parsed?.capacity_kg_per_hour
      ? parsed
      : null;
  } catch {
    return parseBoilerGraphFromRecognizedText(raw);
  }
}
